import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

export type Language = "pt" | "en" | "es" | "fr" | "zh";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isTransitioning: boolean;
}

const detectSystemLanguage = (): Language => {
  const stored = localStorage.getItem("domani-language");
  if (stored && ["pt", "en", "es", "fr", "zh"].includes(stored)) {
    return stored as Language;
  }
  
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith("pt")) return "pt";
  if (browserLang.startsWith("es")) return "es";
  if (browserLang.startsWith("fr")) return "fr";
  if (browserLang.startsWith("zh")) return "zh";
  return "en"; // Default to English for other languages
};

const translations: Record<Language, Record<string, string>> = {
  pt: {
    // Header
    "nav.guide": "Guia Completo",
    "nav.blog": "Blog",
    "nav.install": "Instalar App",
    "nav.language": "Idioma",
    
    // Hero
    "hero.badge": "Sustentabilidade Empresarial",
    "hero.title": "Transforme sua empresa em referência de",
    "hero.titleHighlight": "Sustentabilidade",
    "hero.subtitle": "Soluções completas em ESG, inventário de emissões, créditos de carbono e neutralização para empresas que querem liderar a transição verde.",
    "hero.cta": "Começar Agora",
    "hero.secondary": "Saiba Mais",
    
    // Solutions
    "solutions.badge": "6 Pilares da Sustentabilidade",
    "solutions.title": "Soluções Completas para sua Empresa",
    "solutions.subtitle": "Oferecemos um ecossistema integrado de serviços que cobrem todas as necessidades da sua jornada sustentável.",
    
    // How It Works
    "howItWorks.badge": "Como Funciona",
    "howItWorks.title": "Sua Jornada Sustentável em 4 Passos",
    "howItWorks.subtitle": "Um processo simples e eficiente para transformar sua empresa.",
    "howItWorks.step1.title": "Diagnóstico",
    "howItWorks.step1.desc": "Avaliamos sua situação atual e identificamos oportunidades.",
    "howItWorks.step2.title": "Planejamento",
    "howItWorks.step2.desc": "Criamos uma estratégia personalizada para seus objetivos.",
    "howItWorks.step3.title": "Implementação",
    "howItWorks.step3.desc": "Executamos as ações com acompanhamento contínuo.",
    "howItWorks.step4.title": "Resultados",
    "howItWorks.step4.desc": "Medimos impactos e certificamos suas conquistas.",
    
    // Why Now
    "whyNow.badge": "Por Que Agora?",
    "whyNow.title": "Por Que Isso Importa Agora?",
    "whyNow.subtitle": "O momento de agir é agora. As regulamentações estão mudando e o mercado exige ação.",
    "whyNow.cta": "Comece Sua Jornada Hoje",
    
    // CTA
    "cta.title": "Pronto para Transformar sua Empresa?",
    "cta.subtitle": "Entre em contato e descubra como podemos ajudar sua empresa a se tornar referência em sustentabilidade.",
    "cta.button": "Fale Conosco",
    
    // Footer
    "footer.description": "Soluções completas em sustentabilidade empresarial, ESG e créditos de carbono.",
    "footer.links": "Links Úteis",
    "footer.contact": "Contato",
    "footer.rights": "Todos os direitos reservados.",
    
    // PWA
    "pwa.install": "Instalar Domani",
    "pwa.tagline": "Sustentabilidade na palma da mão",
    "pwa.description": "Acesse o blog e conteúdos sobre sustentabilidade, ESG e créditos de carbono.",
    "pwa.button": "Instalar",
    "pwa.later": "Depois",
    "pwa.iosTitle": "Instalar no iPhone",
    "pwa.understood": "Entendi",
    "pwa.iosStep1": "Toque no ícone de Compartilhar (quadrado com seta para cima)",
    "pwa.iosStep2": "Role e toque em \"Adicionar à Tela de Início\"",
    "pwa.iosStep3": "Toque em \"Adicionar\"",
    "pwa.installApp": "Instalar App",
    "pwa.browserMenu": "Abra o menu do navegador (3 pontos)",
    "pwa.tapInstall": "Toque em \"Instalar app\" ou \"Adicionar à tela inicial\"",
    "pwa.confirm": "Confirme tocando em \"Instalar\"",
    "pwa.shareButton": "Compartilhar",
    "pwa.addToHome": "Adicionar à Tela de Início",
    "pwa.add": "Adicionar",

    // Auth
    "auth.loginTitle": "Entrar no Painel",
    "auth.signupTitle": "Criar Conta",
    "auth.loginSubtitle": "Acesse o painel administrativo",
    "auth.signupSubtitle": "Crie sua conta de administrador",
    "auth.email": "E-mail",
    "auth.password": "Senha",
    "auth.login": "Entrar",
    "auth.signup": "Criar Conta",
    "auth.loading": "Carregando...",
    "auth.noAccount": "Não tem conta? Criar agora",
    "auth.hasAccount": "Já tem conta? Fazer login",
    "auth.backToSite": "← Voltar ao site",
    "auth.fillAllFields": "Por favor, preencha todos os campos.",
    "auth.passwordMinLength": "A senha deve ter pelo menos 6 caracteres.",
    "auth.loginSuccess": "Login realizado com sucesso!",
    "auth.signupSuccess": "Conta criada com sucesso!",
    "auth.invalidCredentials": "Email ou senha incorretos.",
    "auth.userExists": "Este email já está cadastrado.",
    "auth.error": "Erro na autenticação.",

    // NotFound
    "notFound.title": "404",
    "notFound.message": "Ops! Página não encontrada",
    "notFound.backHome": "Voltar ao Início",

    // Blog
    "blog.badge": "Blog & Perguntas",
    "blog.title": "Discussões sobre",
    "blog.titleHighlight": "Sustentabilidade",
    "blog.subtitle": "Tire suas dúvidas sobre ESG, créditos de carbono, mudanças climáticas e muito mais.",
    "blog.search": "Buscar perguntas e respostas...",
    "blog.answeredQuestions": "Perguntas Respondidas",
    "blog.questionsFound": "perguntas encontradas",
    "blog.questionFound": "pergunta encontrada",
    "blog.noResults": "Nenhuma pergunta encontrada com esses filtros.",
    "blog.noQuestions": "Ainda não temos perguntas respondidas. Seja o primeiro a enviar sua dúvida!",
    "blog.asked": "perguntou:",
    "blog.answerBy": "Resposta de Fabiano Santana - Domani:",
    "blog.showLess": "Mostrar menos",
    "blog.readMore": "Ler mais",
    "blog.askQuestion": "Faça sua Pergunta",
    "blog.askSubtitle": "Responderemos publicamente",
    "blog.yourName": "Seu Nome",
    "blog.namePlaceholder": "Como devemos te chamar?",
    "blog.email": "E-mail",
    "blog.emailPlaceholder": "seu@email.com",
    "blog.phone": "Telefone",
    "blog.phonePlaceholder": "+55 (11) 99999-9999",
    "blog.contactNote": "Preencha pelo menos e-mail ou telefone para contato.",
    "blog.yourQuestion": "Sua Pergunta",
    "blog.questionPlaceholder": "Qual sua dúvida sobre sustentabilidade, ESG ou carbono?",
    "blog.submit": "Enviar Pergunta",
    "blog.submitting": "Enviando...",
    "blog.submitNote": "Sua pergunta será respondida por Fabiano Santana e publicada para ajudar outras pessoas.",
    "blog.successMessage": "Pergunta enviada com sucesso! Responderemos em breve.",
    "blog.errorMessage": "Erro ao enviar pergunta. Tente novamente.",
    "blog.validationError": "Por favor, preencha seu nome, pergunta e pelo menos email ou telefone.",
    "blog.translating": "Traduzindo conteúdo...",
    "blog.translatingDesc": "Aguarde enquanto traduzimos as perguntas e respostas",
    "blog.breadcrumb": "Blog ESG",
    
    // Categories
    "cat.all": "Todos",
    "cat.sustainability": "Sustentabilidade",
    "cat.esg": "ESG",
    "cat.climate": "Clima",
    "cat.emissions": "Emissões",
    "cat.carbon": "Carbono",
    "cat.energy": "Energia",
    "cat.economy": "Economia",
    "cat.practice": "Prática",

    // Solutions Section
    "solutions.sectionTitle": "Os 6 Pilares da Sustentabilidade Empresarial",
    "solutions.sectionSubtitle": "Explicados de forma simples, com exemplos do mundo real",
    "solutions.whatIs": "O Que É?",
    "solutions.problemSolves": "Qual Problema Resolve?",
    "solutions.valueGenerated": "Valor Gerado",
    
    // Solution 1 - GHG Inventory
    "sol1.title": "Inventário de GEE: O Raio-X da Poluição",
    "sol1.subtitle": "Gases de Efeito Estufa",
    "sol1.whatIs": "É como fazer um \"raio-X da poluição\" da empresa - descobrir quanto de gases poluentes (como CO₂) ela emite em tudo que faz.",
    "sol1.problem": "Sem saber quanto polui, uma empresa é como alguém tentando emagrecer sem se pesar. Não tem como melhorar o que você não mede.",
    "sol1.exampleTitle": "Exemplo Simples",
    "sol1.exampleIntro": "Uma transportadora tem 100 caminhões. O Domani SaaS mede automaticamente:",
    "sol1.exampleItem1": "Escopo 1: Combustível queimado pelos caminhões (emissão direta)",
    "sol1.exampleItem2": "Escopo 2: Energia elétrica usada nos escritórios",
    "sol1.exampleItem3": "Escopo 3: Emissões dos fornecedores de pneus, peças, até a produção do diesel",
    "sol1.benefit1": "Economia de tempo: Reduz 70% do tempo gasto em relatórios",
    "sol1.benefit2": "Dinheiro: Evita multas e prepara para leis obrigatórias (2025/2026)",
    "sol1.benefit3": "Competitividade: Grandes empresas só trabalham com fornecedores que medem suas emissões",

    // Solution 2 - ESG
    "sol2.title": "ESG: O Boletim Escolar da Empresa",
    "sol2.subtitle": "Ambiental, Social e Governança",
    "sol2.whatIs": "É um \"boletim escolar\" da empresa em 3 matérias: meio ambiente, tratamento de pessoas e ética nos negócios.",
    "sol2.problem": "Investidores, bancos e clientes grandes só fazem negócio com empresas \"bem comportadas\". Sem ESG, você fica de fora de contratos milionários.",
    "sol2.exampleTitle": "Exemplo Simples",
    "sol2.exampleIntro": "Uma fábrica de móveis quer vender para uma grande rede de lojas. A rede exige:",
    "sol2.exampleItem1": "E (Ambiental): Prove que não desmata ilegalmente",
    "sol2.exampleItem2": "S (Social): Mostre que não usa trabalho infantil",
    "sol2.exampleItem3": "G (Governança): Tenha processos transparentes e anti-corrupção",
    "sol2.benefit1": "Acesso a crédito: Bancos oferecem juros 20-30% menores para empresas ESG",
    "sol2.benefit2": "Novos contratos: 66% das grandes empresas globais só compram de fornecedores sustentáveis",
    "sol2.benefit3": "Imagem: Consumidores pagam até 15% mais por produtos de marcas sustentáveis",

    // Solution 3 - Neutralization
    "sol3.title": "Neutralização de Emissões: Zerando sua Dívida",
    "sol3.subtitle": "Compensação de Carbono",
    "sol3.whatIs": "É como plantar árvores para \"compensar\" a poluição que você não consegue eliminar ainda.",
    "sol3.problem": "Mesmo fazendo o melhor possível, toda empresa ainda polui um pouco. A compensação zera essa conta e permite dizer \"sou carbono neutro\".",
    "sol3.exampleTitle": "Exemplo Simples",
    "sol3.exampleIntro": "Uma empresa de eventos calcula que um festival emitiu 50 toneladas de CO₂ (transporte do público, energia, etc.):",
    "sol3.exampleItem1": "Pelo Domani SaaS, ela compra \"créditos de carbono\" equivalentes a 50 toneladas",
    "sol3.exampleItem2": "Cada crédito representa 1 tonelada de CO₂ evitada ou removida da atmosfera",
    "sol3.exampleItem3": "Créditos vêm de projetos de reflorestamento ou energia limpa",
    "sol3.benefit1": "Marketing poderoso: \"Evento Carbono Neutro\" atrai +40% de público consciente",
    "sol3.benefit2": "Diferenciação: Vira case de sucesso e referência no setor",
    "sol3.benefit3": "Preparação legal: Antecipar-se às leis de mercado de carbono",

    // Solution 4 - Carbon Credits
    "sol4.title": "Projetos de Créditos de Carbono: Transformando Sustentabilidade em Receita",
    "sol4.subtitle": "Monetize Suas Ações Verdes",
    "sol4.whatIs": "Transformar ações sustentáveis em \"moeda ambiental\" que pode ser vendida.",
    "sol4.problem": "Propriedades rurais/empresas com florestas ou projetos de energia limpa têm um ativo parado - podem gerar receita nova vendendo créditos.",
    "sol4.exampleTitle": "Exemplo Simples",
    "sol4.exampleIntro": "Um fazendeiro tem 200 hectares de floresta preservada. O Moss Forest by Domani:",
    "sol4.exampleItem1": "Mede quanto CO₂ aquela floresta absorve por ano",
    "sol4.exampleItem2": "Certifica o projeto (VERRA, CPR Verde)",
    "sol4.exampleItem3": "Gera créditos de carbono e vende para empresas que precisam compensar emissões",
    "sol4.benefit1": "Receita recorrente: Créditos podem ser vendidos anualmente",
    "sol4.benefit2": "Valorização: Terra com projeto de carbono vale 30-50% mais",
    "sol4.benefit3": "Conservação: Incentivo financeiro para não desmatar",
    "sol4.highlight": "Resultado: O fazendeiro ganha R$ 500 mil/ano sem derrubar uma árvore.",

    // Solution 5 - Monitoring
    "sol5.title": "Monitoramento por Satélite: Vigilância Inteligente",
    "sol5.subtitle": "Vigilância por Satélite + IA",
    "sol5.whatIs": "Uso de satélites e inteligência artificial para vigiar fazendas em tempo real e validar documentos ambientais.",
    "sol5.problem": "Evitar fraudes (fazendeiro diz que preserva, mas desmata escondido) e eliminar vistorias presenciais caras e demoradas.",
    "sol5.exampleTitle": "Exemplo Simples",
    "sol5.exampleIntro": "Um banco vai emprestar R$ 10 milhões para um produtor de soja. Antes de liberar o dinheiro, usa o Moss Forest by Domani para:",
    "sol5.exampleItem1": "Ver por satélite se a área está realmente preservada",
    "sol5.exampleItem2": "Verificar se tem CAR (Cadastro Ambiental Rural) válido",
    "sol5.exampleItem3": "Alertar automaticamente se houver desmatamento durante o contrato",
    "sol5.benefit1": "Redução de custos: Elimina 90% das vistorias físicas",
    "sol5.benefit2": "Velocidade: Análise que levava 30 dias cai para 24 horas",
    "sol5.benefit3": "Segurança: Monitoramento contínuo vs. vistoria única por ano",
    "sol5.highlight": "Resultado: Banco reduz risco de emprestar para quem polui (e sofrer multa).",

    // Solution 6 - Agricultural Credit
    "sol6.title": "Análise de Crédito Agrícola: Parceria Inteligente",
    "sol6.subtitle": "Crédito Inteligente e Sustentável",
    "sol6.whatIs": "É como ter um \"consultor inteligente que não apenas aprova ou rejeita, mas ajuda o agricultor a se organizar\". A Domani faz análise documental automática e, se algo está incompleto ou errado, sugere exatamente o que fazer para corrigir. Assim, agente de crédito e agricultor trabalham juntos para criar uma relação de confiança e parceria.",
    "sol6.problem": "Bancos levam 30-45 dias analisando documentos manualmente. Se estão incompletos, o agricultor recebe rejeição sem orientação, não entende o que fez \"errado\" e desiste. Com Domani: análise em 24-48h, sugestões claras de correção, e agricultor vira cliente melhor preparado.",
    "sol6.exampleTitle": "Exemplo Detalhado: João, Produtor de Soja",
    "sol6.exampleIntro": "João quer R$ 800 mil para sementes e fertilizantes. Veja como funciona:",
    "sol6.exampleItem1": "Dia 1: João envia documentos (CPF, CAR, Licença Ambiental expirada, Extrato incompleto)",
    "sol6.exampleItem2": "Em 4 horas: IA analisa tudo e identifica: Licença expirada + Extrato só 3 meses (deveria ter 12)",
    "sol6.exampleItem3": "Relatório inteligente: \"Status 85% completo | Perfil BOM\" com instruções claras de correção",
    "sol6.exampleItem4": "Agente liga: \"João, você foi APROVADO! São só 2 coisinhas: renovar Licença na SEMMA (15 dias) e pedir extrato 12 meses\"",
    "sol6.exampleItem5": "Dias 2-10: João corrige tudo com orientação do agente",
    "sol6.exampleItem6": "Dia 11-12: Domani valida automaticamente → CRÉDITO LIBERADO em 2-3 dias úteis",
    "sol6.benefit1": "Para o Agricultor: Clareza (entende o que fazer), orientação passo a passo, 10-12 dias vs. 45 dias tradicional",
    "sol6.benefit2": "Para o Agente: Não gasta 40+ horas por cliente, orienta 10-12 clientes/dia (vs. 2 manualmente)",
    "sol6.benefit3": "Para o Banco: Reduz inadimplência em 35%, analisa 5-10x mais clientes, conformidade ESG comprovada",
    "sol6.highlight": "Resultado: Tempo de análise cai 92% (45 dias → 2-3 dias), produtividade 5x maior, satisfação do agricultor sobe de 60% para 95%.",

    // HowItWorks Section
    "how.sectionTitle": "Como Tudo Isso Se Conecta?",
    "how.sectionSubtitle": "Pense na Domani como uma",
    "how.sectionSubtitleBold": "plataforma completa de sustentabilidade",
    "how.step1.title": "Mede",
    "how.step1.desc": "Sua poluição",
    "how.step1.detail": "Inventário GEE",
    "how.step2.title": "Organiza",
    "how.step2.desc": "Suas boas práticas",
    "how.step2.detail": "ESG",
    "how.step3.title": "Compensa",
    "how.step3.desc": "O que não pode evitar",
    "how.step3.detail": "Neutralização",
    "how.step4.title": "Monetiza",
    "how.step4.desc": "Suas ações verdes",
    "how.step4.detail": "Créditos de Carbono",
    "how.step5.title": "Protege",
    "how.step5.desc": "Seus ativos ambientais",
    "how.step5.detail": "Monitoramento",
    "how.step6.title": "Facilita",
    "how.step6.desc": "Financiamento sustentável",
    "how.step6.detail": "Análise de Crédito",
    "how.summaryPart1": "A Domani transforma sustentabilidade de",
    "how.summaryBold1": "custo obrigatório",
    "how.summaryPart2": "em",
    "how.summaryBold2": "vantagem competitiva rentável",
    "how.summaryPart3": ", usando tecnologia para tornar o",
    "how.summaryBold3": "complexo simples",
    "how.summaryPart4": "e o",
    "how.summaryBold4": "invisível mensurável",

    // WhyNow Section
    "why.badge": "Urgente",
    "why.title": "Por Que Isso Importa",
    "why.titleHighlight": "Agora",
    "why.reason1.title": "Leis Chegando",
    "why.reason1.desc": "A partir de 2026, empresas brasileiras serão obrigadas a reportar emissões de gases de efeito estufa.",
    "why.reason2.title": "Pressão de Mercado",
    "why.reason2.desc": "Grandes empresas já exigem sustentabilidade de fornecedores. Quem não se adaptar, fica de fora.",
    "why.reason3.title": "Oportunidade Financeira",
    "why.reason3.desc": "Créditos de carbono movimentarão R$ 100+ bilhões/ano no Brasil. É hora de se posicionar.",
    "why.conclusion": "O momento de agir é",
    "why.conclusionBold": "agora",
    "why.conclusionEnd": ". Empresas que se antecipam ganham vantagem competitiva e evitam correria de última hora.",

    // Network Status
    "network.offline": "Você está offline. Verifique sua conexão.",
    "network.online": "Conexão restaurada!",

    // Share
    "share.button": "Compartilhar",
    "share.copied": "Link copiado para a área de transferência!",

    // Pull to Refresh
    "pullToRefresh.release": "Solte para atualizar",
    "pullToRefresh.pull": "Puxe para atualizar",
  },
  en: {
    // Header
    "nav.guide": "Complete Guide",
    "nav.blog": "Blog",
    "nav.install": "Install App",
    "nav.language": "Language",
    
    // Hero
    "hero.badge": "Business Sustainability",
    "hero.title": "Transform your company into a reference in",
    "hero.titleHighlight": "Sustainability",
    "hero.subtitle": "Complete solutions in ESG, emissions inventory, carbon credits and neutralization for companies that want to lead the green transition.",
    "hero.cta": "Get Started",
    "hero.secondary": "Learn More",
    
    // Solutions
    "solutions.badge": "6 Pillars of Sustainability",
    "solutions.title": "Complete Solutions for Your Business",
    "solutions.subtitle": "We offer an integrated ecosystem of services that cover all the needs of your sustainable journey.",
    
    // How It Works
    "howItWorks.badge": "How It Works",
    "howItWorks.title": "Your Sustainable Journey in 4 Steps",
    "howItWorks.subtitle": "A simple and efficient process to transform your company.",
    "howItWorks.step1.title": "Diagnosis",
    "howItWorks.step1.desc": "We assess your current situation and identify opportunities.",
    "howItWorks.step2.title": "Planning",
    "howItWorks.step2.desc": "We create a personalized strategy for your goals.",
    "howItWorks.step3.title": "Implementation",
    "howItWorks.step3.desc": "We execute actions with continuous monitoring.",
    "howItWorks.step4.title": "Results",
    "howItWorks.step4.desc": "We measure impacts and certify your achievements.",
    
    // Why Now
    "whyNow.badge": "Why Now?",
    "whyNow.title": "Why Does This Matter Now?",
    "whyNow.subtitle": "The time to act is now. Regulations are changing and the market demands action.",
    "whyNow.cta": "Start Your Journey Today",
    
    // CTA
    "cta.title": "Ready to Transform Your Company?",
    "cta.subtitle": "Get in touch and discover how we can help your company become a reference in sustainability.",
    "cta.button": "Contact Us",
    
    // Footer
    "footer.description": "Complete solutions in business sustainability, ESG and carbon credits.",
    "footer.links": "Useful Links",
    "footer.contact": "Contact",
    "footer.rights": "All rights reserved.",
    
    // PWA
    "pwa.install": "Install Domani",
    "pwa.tagline": "Sustainability at your fingertips",
    "pwa.description": "Access the blog and content about sustainability, ESG and carbon credits.",
    "pwa.button": "Install",
    "pwa.later": "Later",
    "pwa.iosTitle": "Install on iPhone",
    "pwa.understood": "Got it",
    "pwa.iosStep1": "Tap the Share icon (square with arrow pointing up)",
    "pwa.iosStep2": "Scroll and tap \"Add to Home Screen\"",
    "pwa.iosStep3": "Tap \"Add\"",
    "pwa.installApp": "Install App",
    "pwa.browserMenu": "Open browser menu (3 dots)",
    "pwa.tapInstall": "Tap \"Install app\" or \"Add to home screen\"",
    "pwa.confirm": "Confirm by tapping \"Install\"",
    "pwa.shareButton": "Share",
    "pwa.addToHome": "Add to Home Screen",
    "pwa.add": "Add",

    // Auth
    "auth.loginTitle": "Login to Panel",
    "auth.signupTitle": "Create Account",
    "auth.loginSubtitle": "Access the admin panel",
    "auth.signupSubtitle": "Create your admin account",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.login": "Login",
    "auth.signup": "Create Account",
    "auth.loading": "Loading...",
    "auth.noAccount": "No account? Create one",
    "auth.hasAccount": "Have an account? Login",
    "auth.backToSite": "← Back to site",
    "auth.fillAllFields": "Please fill in all fields.",
    "auth.passwordMinLength": "Password must be at least 6 characters.",
    "auth.loginSuccess": "Login successful!",
    "auth.signupSuccess": "Account created successfully!",
    "auth.invalidCredentials": "Invalid email or password.",
    "auth.userExists": "This email is already registered.",
    "auth.error": "Authentication error.",

    // NotFound
    "notFound.title": "404",
    "notFound.message": "Oops! Page not found",
    "notFound.backHome": "Return to Home",

    // Blog
    "blog.badge": "Blog & Questions",
    "blog.title": "Discussions about",
    "blog.titleHighlight": "Sustainability",
    "blog.subtitle": "Get your questions answered about ESG, carbon credits, climate change and more.",
    "blog.search": "Search questions and answers...",
    "blog.answeredQuestions": "Answered Questions",
    "blog.questionsFound": "questions found",
    "blog.questionFound": "question found",
    "blog.noResults": "No questions found with these filters.",
    "blog.noQuestions": "No answered questions yet. Be the first to submit your question!",
    "blog.asked": "asked:",
    "blog.answerBy": "Answer by Fabiano Santana - Domani:",
    "blog.showLess": "Show less",
    "blog.readMore": "Read more",
    "blog.askQuestion": "Ask a Question",
    "blog.askSubtitle": "We'll answer publicly",
    "blog.yourName": "Your Name",
    "blog.namePlaceholder": "What should we call you?",
    "blog.email": "Email",
    "blog.emailPlaceholder": "your@email.com",
    "blog.phone": "Phone",
    "blog.phonePlaceholder": "+1 (555) 123-4567",
    "blog.contactNote": "Please provide at least an email or phone for contact.",
    "blog.yourQuestion": "Your Question",
    "blog.questionPlaceholder": "What's your question about sustainability, ESG or carbon?",
    "blog.submit": "Submit Question",
    "blog.submitting": "Submitting...",
    "blog.submitNote": "Your question will be answered by Fabiano Santana and published to help others.",
    "blog.successMessage": "Question submitted successfully! We'll respond soon.",
    "blog.errorMessage": "Error submitting question. Please try again.",
    "blog.validationError": "Please fill in your name, question and at least email or phone.",
    "blog.translating": "Translating content...",
    "blog.translatingDesc": "Please wait while we translate the questions and answers",
    "blog.breadcrumb": "ESG Blog",
    
    // Categories
    "cat.all": "All",
    "cat.sustainability": "Sustainability",
    "cat.esg": "ESG",
    "cat.climate": "Climate",
    "cat.emissions": "Emissions",
    "cat.carbon": "Carbon",
    "cat.energy": "Energy",
    "cat.economy": "Economy",
    "cat.practice": "Practice",

    // Solutions Section
    "solutions.sectionTitle": "The 6 Pillars of Business Sustainability",
    "solutions.sectionSubtitle": "Explained simply, with real-world examples",
    "solutions.whatIs": "What Is It?",
    "solutions.problemSolves": "What Problem Does It Solve?",
    "solutions.valueGenerated": "Value Generated",
    
    // Solution 1 - GHG Inventory
    "sol1.title": "GHG Inventory: The Pollution X-Ray",
    "sol1.subtitle": "Greenhouse Gases",
    "sol1.whatIs": "It's like doing a \"pollution X-ray\" of the company - finding out how much polluting gases (like CO₂) it emits in everything it does.",
    "sol1.problem": "Without knowing how much it pollutes, a company is like someone trying to lose weight without weighing themselves. You can't improve what you don't measure.",
    "sol1.exampleTitle": "Simple Example",
    "sol1.exampleIntro": "A transport company has 100 trucks. Domani SaaS automatically measures:",
    "sol1.exampleItem1": "Scope 1: Fuel burned by trucks (direct emission)",
    "sol1.exampleItem2": "Scope 2: Electricity used in offices",
    "sol1.exampleItem3": "Scope 3: Emissions from tire suppliers, parts, even diesel production",
    "sol1.benefit1": "Time savings: Reduces 70% of time spent on reports",
    "sol1.benefit2": "Money: Avoids fines and prepares for mandatory laws (2025/2026)",
    "sol1.benefit3": "Competitiveness: Large companies only work with suppliers that measure their emissions",

    // Solution 2 - ESG
    "sol2.title": "ESG: The Company's Report Card",
    "sol2.subtitle": "Environmental, Social and Governance",
    "sol2.whatIs": "It's a company \"report card\" in 3 subjects: environment, treatment of people and business ethics.",
    "sol2.problem": "Investors, banks and large clients only do business with \"well-behaved\" companies. Without ESG, you're left out of million-dollar contracts.",
    "sol2.exampleTitle": "Simple Example",
    "sol2.exampleIntro": "A furniture factory wants to sell to a large retail chain. The chain requires:",
    "sol2.exampleItem1": "E (Environmental): Prove you don't deforest illegally",
    "sol2.exampleItem2": "S (Social): Show you don't use child labor",
    "sol2.exampleItem3": "G (Governance): Have transparent and anti-corruption processes",
    "sol2.benefit1": "Access to credit: Banks offer 20-30% lower interest for ESG companies",
    "sol2.benefit2": "New contracts: 66% of large global companies only buy from sustainable suppliers",
    "sol2.benefit3": "Image: Consumers pay up to 15% more for sustainable brand products",

    // Solution 3 - Neutralization
    "sol3.title": "Emissions Neutralization: Zeroing Your Debt",
    "sol3.subtitle": "Carbon Offsetting",
    "sol3.whatIs": "It's like planting trees to \"offset\" the pollution you can't eliminate yet.",
    "sol3.problem": "Even doing the best possible, every company still pollutes a little. Offsetting zeroes this account and allows you to say \"I'm carbon neutral\".",
    "sol3.exampleTitle": "Simple Example",
    "sol3.exampleIntro": "An events company calculates that a festival emitted 50 tons of CO₂ (audience transport, energy, etc.):",
    "sol3.exampleItem1": "Through Domani SaaS, it buys \"carbon credits\" equivalent to 50 tons",
    "sol3.exampleItem2": "Each credit represents 1 ton of CO₂ avoided or removed from the atmosphere",
    "sol3.exampleItem3": "Credits come from reforestation or clean energy projects",
    "sol3.benefit1": "Powerful marketing: \"Carbon Neutral Event\" attracts +40% conscious audience",
    "sol3.benefit2": "Differentiation: Becomes a success case and sector reference",
    "sol3.benefit3": "Legal preparation: Anticipate carbon market laws",

    // Solution 4 - Carbon Credits
    "sol4.title": "Carbon Credit Projects: Turning Sustainability into Revenue",
    "sol4.subtitle": "Monetize Your Green Actions",
    "sol4.whatIs": "Transforming sustainable actions into \"environmental currency\" that can be sold.",
    "sol4.problem": "Rural properties/companies with forests or clean energy projects have an idle asset - they can generate new revenue by selling credits.",
    "sol4.exampleTitle": "Simple Example",
    "sol4.exampleIntro": "A farmer has 200 hectares of preserved forest. Moss Forest by Domani:",
    "sol4.exampleItem1": "Measures how much CO₂ that forest absorbs per year",
    "sol4.exampleItem2": "Certifies the project (VERRA, CPR Verde)",
    "sol4.exampleItem3": "Generates carbon credits and sells to companies needing to offset emissions",
    "sol4.benefit1": "Recurring revenue: Credits can be sold annually",
    "sol4.benefit2": "Appreciation: Land with carbon project is worth 30-50% more",
    "sol4.benefit3": "Conservation: Financial incentive not to deforest",
    "sol4.highlight": "Result: The farmer earns $100,000/year without cutting down a tree.",

    // Solution 5 - Monitoring
    "sol5.title": "Satellite Monitoring: Smart Surveillance",
    "sol5.subtitle": "Satellite Surveillance + AI",
    "sol5.whatIs": "Use of satellites and artificial intelligence to monitor farms in real time and validate environmental documents.",
    "sol5.problem": "Avoid fraud (farmer says they preserve, but secretly deforests) and eliminate expensive and time-consuming on-site inspections.",
    "sol5.exampleTitle": "Simple Example",
    "sol5.exampleIntro": "A bank will lend $2 million to a soy producer. Before releasing the money, it uses Moss Forest by Domani to:",
    "sol5.exampleItem1": "See by satellite if the area is really preserved",
    "sol5.exampleItem2": "Verify if it has valid CAR (Rural Environmental Registry)",
    "sol5.exampleItem3": "Automatically alert if there's deforestation during the contract",
    "sol5.benefit1": "Cost reduction: Eliminates 90% of physical inspections",
    "sol5.benefit2": "Speed: Analysis that took 30 days drops to 24 hours",
    "sol5.benefit3": "Security: Continuous monitoring vs. single annual inspection",
    "sol5.highlight": "Result: Bank reduces risk of lending to polluters (and suffering fines).",

    // Solution 6 - Agricultural Credit
    "sol6.title": "Agricultural Credit Analysis: Smart Partnership",
    "sol6.subtitle": "Smart and Sustainable Credit",
    "sol6.whatIs": "It's like having a \"smart consultant who doesn't just approve or reject, but helps the farmer get organized\". Domani does automatic document analysis and, if something is incomplete or wrong, suggests exactly what to do to fix it. This way, credit agent and farmer work together to build a relationship of trust and partnership.",
    "sol6.problem": "Banks take 30-45 days analyzing documents manually. If incomplete, the farmer receives rejection without guidance, doesn't understand what they did \"wrong\" and gives up. With Domani: analysis in 24-48h, clear correction suggestions, and farmer becomes a better-prepared client.",
    "sol6.exampleTitle": "Detailed Example: John, Soy Producer",
    "sol6.exampleIntro": "John wants $160,000 for seeds and fertilizers. Here's how it works:",
    "sol6.exampleItem1": "Day 1: John sends documents (ID, CAR, expired Environmental License, incomplete Statement)",
    "sol6.exampleItem2": "In 4 hours: AI analyzes everything and identifies: Expired license + Statement only 3 months (should be 12)",
    "sol6.exampleItem3": "Smart report: \"Status 85% complete | GOOD Profile\" with clear correction instructions",
    "sol6.exampleItem4": "Agent calls: \"John, you're APPROVED! Just 2 things: renew License at SEMMA (15 days) and request 12-month statement\"",
    "sol6.exampleItem5": "Days 2-10: John fixes everything with agent guidance",
    "sol6.exampleItem6": "Day 11-12: Domani validates automatically → CREDIT RELEASED in 2-3 business days",
    "sol6.benefit1": "For the Farmer: Clarity (understands what to do), step-by-step guidance, 10-12 days vs. 45 traditional days",
    "sol6.benefit2": "For the Agent: Doesn't spend 40+ hours per client, guides 10-12 clients/day (vs. 2 manually)",
    "sol6.benefit3": "For the Bank: Reduces defaults by 35%, analyzes 5-10x more clients, proven ESG compliance",
    "sol6.highlight": "Result: Analysis time drops 92% (45 days → 2-3 days), 5x higher productivity, farmer satisfaction rises from 60% to 95%.",

    // HowItWorks Section
    "how.sectionTitle": "How Does It All Connect?",
    "how.sectionSubtitle": "Think of Domani as a",
    "how.sectionSubtitleBold": "complete sustainability platform",
    "how.step1.title": "Measures",
    "how.step1.desc": "Your pollution",
    "how.step1.detail": "GHG Inventory",
    "how.step2.title": "Organizes",
    "how.step2.desc": "Your best practices",
    "how.step2.detail": "ESG",
    "how.step3.title": "Offsets",
    "how.step3.desc": "What you can't avoid",
    "how.step3.detail": "Neutralization",
    "how.step4.title": "Monetizes",
    "how.step4.desc": "Your green actions",
    "how.step4.detail": "Carbon Credits",
    "how.step5.title": "Protects",
    "how.step5.desc": "Your environmental assets",
    "how.step5.detail": "Monitoring",
    "how.step6.title": "Facilitates",
    "how.step6.desc": "Sustainable financing",
    "how.step6.detail": "Credit Analysis",
    "how.summaryPart1": "Domani transforms sustainability from",
    "how.summaryBold1": "mandatory cost",
    "how.summaryPart2": "into",
    "how.summaryBold2": "profitable competitive advantage",
    "how.summaryPart3": ", using technology to make the",
    "how.summaryBold3": "complex simple",
    "how.summaryPart4": "and the",
    "how.summaryBold4": "invisible measurable",

    // WhyNow Section
    "why.badge": "Urgent",
    "why.title": "Why Does This Matter",
    "why.titleHighlight": "Now",
    "why.reason1.title": "Laws Coming",
    "why.reason1.desc": "From 2026, Brazilian companies will be required to report greenhouse gas emissions.",
    "why.reason2.title": "Market Pressure",
    "why.reason2.desc": "Large companies already demand sustainability from suppliers. Those who don't adapt are left out.",
    "why.reason3.title": "Financial Opportunity",
    "why.reason3.desc": "Carbon credits will move $20+ billion/year in Brazil. It's time to position yourself.",
    "why.conclusion": "The time to act is",
    "why.conclusionBold": "now",
    "why.conclusionEnd": ". Companies that anticipate gain competitive advantage and avoid last-minute rushing.",

    // Network Status
    "network.offline": "You are offline. Check your connection.",
    "network.online": "Connection restored!",

    // Share
    "share.button": "Share",
    "share.copied": "Link copied to clipboard!",

    // Pull to Refresh
    "pullToRefresh.release": "Release to refresh",
    "pullToRefresh.pull": "Pull to refresh",
  },
  es: {
    // Header
    "nav.guide": "Guía Completa",
    "nav.blog": "Blog",
    "nav.install": "Instalar App",
    "nav.language": "Idioma",
    
    // Hero
    "hero.badge": "Sostenibilidad Empresarial",
    "hero.title": "Transforma tu empresa en referencia de",
    "hero.titleHighlight": "Sostenibilidad",
    "hero.subtitle": "Soluciones completas en ESG, inventario de emisiones, créditos de carbono y neutralización para empresas que quieren liderar la transición verde.",
    "hero.cta": "Empezar Ahora",
    "hero.secondary": "Saber Más",
    
    // Solutions
    "solutions.badge": "6 Pilares de la Sostenibilidad",
    "solutions.title": "Soluciones Completas para tu Empresa",
    "solutions.subtitle": "Ofrecemos un ecosistema integrado de servicios que cubren todas las necesidades de tu viaje sostenible.",
    
    // How It Works
    "howItWorks.badge": "Cómo Funciona",
    "howItWorks.title": "Tu Viaje Sostenible en 4 Pasos",
    "howItWorks.subtitle": "Un proceso simple y eficiente para transformar tu empresa.",
    "howItWorks.step1.title": "Diagnóstico",
    "howItWorks.step1.desc": "Evaluamos tu situación actual e identificamos oportunidades.",
    "howItWorks.step2.title": "Planificación",
    "howItWorks.step2.desc": "Creamos una estrategia personalizada para tus objetivos.",
    "howItWorks.step3.title": "Implementación",
    "howItWorks.step3.desc": "Ejecutamos las acciones con seguimiento continuo.",
    "howItWorks.step4.title": "Resultados",
    "howItWorks.step4.desc": "Medimos impactos y certificamos tus logros.",
    
    // Why Now
    "whyNow.badge": "¿Por Qué Ahora?",
    "whyNow.title": "¿Por Qué Esto Importa Ahora?",
    "whyNow.subtitle": "El momento de actuar es ahora. Las regulaciones están cambiando y el mercado exige acción.",
    "whyNow.cta": "Comienza Tu Viaje Hoy",
    
    // CTA
    "cta.title": "¿Listo para Transformar tu Empresa?",
    "cta.subtitle": "Ponte en contacto y descubre cómo podemos ayudar a tu empresa a convertirse en referencia en sostenibilidad.",
    "cta.button": "Contáctanos",
    
    // Footer
    "footer.description": "Soluciones completas en sostenibilidad empresarial, ESG y créditos de carbono.",
    "footer.links": "Enlaces Útiles",
    "footer.contact": "Contacto",
    "footer.rights": "Todos los derechos reservados.",
    
    // PWA
    "pwa.install": "Instalar Domani",
    "pwa.tagline": "Sostenibilidad en tu mano",
    "pwa.description": "Accede al blog y contenidos sobre sostenibilidad, ESG y créditos de carbono.",
    "pwa.button": "Instalar",
    "pwa.later": "Después",
    "pwa.iosTitle": "Instalar en iPhone",
    "pwa.understood": "Entendido",
    "pwa.iosStep1": "Toca el ícono de Compartir (cuadrado con flecha hacia arriba)",
    "pwa.iosStep2": "Desplázate y toca \"Añadir a la pantalla de inicio\"",
    "pwa.iosStep3": "Toca \"Añadir\"",
    "pwa.installApp": "Instalar App",
    "pwa.browserMenu": "Abre el menú del navegador (3 puntos)",
    "pwa.tapInstall": "Toca \"Instalar app\" o \"Añadir a pantalla de inicio\"",
    "pwa.confirm": "Confirma tocando \"Instalar\"",
    "pwa.shareButton": "Compartir",
    "pwa.addToHome": "Añadir a la pantalla de inicio",
    "pwa.add": "Añadir",

    // Auth
    "auth.loginTitle": "Iniciar Sesión",
    "auth.signupTitle": "Crear Cuenta",
    "auth.loginSubtitle": "Accede al panel administrativo",
    "auth.signupSubtitle": "Crea tu cuenta de administrador",
    "auth.email": "Correo",
    "auth.password": "Contraseña",
    "auth.login": "Iniciar Sesión",
    "auth.signup": "Crear Cuenta",
    "auth.loading": "Cargando...",
    "auth.noAccount": "¿No tienes cuenta? Crear ahora",
    "auth.hasAccount": "¿Ya tienes cuenta? Iniciar sesión",
    "auth.backToSite": "← Volver al sitio",
    "auth.fillAllFields": "Por favor, completa todos los campos.",
    "auth.passwordMinLength": "La contraseña debe tener al menos 6 caracteres.",
    "auth.loginSuccess": "¡Inicio de sesión exitoso!",
    "auth.signupSuccess": "¡Cuenta creada con éxito!",
    "auth.invalidCredentials": "Correo o contraseña incorrectos.",
    "auth.userExists": "Este correo ya está registrado.",
    "auth.error": "Error de autenticación.",

    // NotFound
    "notFound.title": "404",
    "notFound.message": "¡Ups! Página no encontrada",
    "notFound.backHome": "Volver al Inicio",

    // Blog
    "blog.badge": "Blog & Preguntas",
    "blog.title": "Discusiones sobre",
    "blog.titleHighlight": "Sostenibilidad",
    "blog.subtitle": "Resuelve tus dudas sobre ESG, créditos de carbono, cambio climático y más.",
    "blog.search": "Buscar preguntas y respuestas...",
    "blog.answeredQuestions": "Preguntas Respondidas",
    "blog.questionsFound": "preguntas encontradas",
    "blog.questionFound": "pregunta encontrada",
    "blog.noResults": "No se encontraron preguntas con estos filtros.",
    "blog.noQuestions": "Aún no tenemos preguntas respondidas. ¡Sé el primero en enviar tu duda!",
    "blog.asked": "preguntó:",
    "blog.answerBy": "Respuesta de Fabiano Santana - Domani:",
    "blog.showLess": "Mostrar menos",
    "blog.readMore": "Leer más",
    "blog.askQuestion": "Haz tu Pregunta",
    "blog.askSubtitle": "Responderemos públicamente",
    "blog.yourName": "Tu Nombre",
    "blog.namePlaceholder": "¿Cómo debemos llamarte?",
    "blog.email": "Correo",
    "blog.emailPlaceholder": "tu@email.com",
    "blog.phone": "Teléfono",
    "blog.phonePlaceholder": "+34 612 345 678",
    "blog.contactNote": "Completa al menos correo o teléfono para contacto.",
    "blog.yourQuestion": "Tu Pregunta",
    "blog.questionPlaceholder": "¿Cuál es tu duda sobre sostenibilidad, ESG o carbono?",
    "blog.submit": "Enviar Pregunta",
    "blog.submitting": "Enviando...",
    "blog.submitNote": "Tu pregunta será respondida por Fabiano Santana y publicada para ayudar a otros.",
    "blog.successMessage": "¡Pregunta enviada con éxito! Responderemos pronto.",
    "blog.errorMessage": "Error al enviar pregunta. Intenta de nuevo.",
    "blog.validationError": "Por favor, completa tu nombre, pregunta y al menos correo o teléfono.",
    "blog.translating": "Traduciendo contenido...",
    "blog.translatingDesc": "Por favor espera mientras traducimos las preguntas y respuestas",
    "blog.breadcrumb": "Blog ESG",
    
    // Categories
    "cat.all": "Todos",
    "cat.sustainability": "Sostenibilidad",
    "cat.esg": "ESG",
    "cat.climate": "Clima",
    "cat.emissions": "Emisiones",
    "cat.carbon": "Carbono",
    "cat.energy": "Energía",
    "cat.economy": "Economía",
    "cat.practice": "Práctica",

    // Solutions Section
    "solutions.sectionTitle": "Los 6 Pilares de la Sostenibilidad Empresarial",
    "solutions.sectionSubtitle": "Explicados de forma simple, con ejemplos del mundo real",
    "solutions.whatIs": "¿Qué Es?",
    "solutions.problemSolves": "¿Qué Problema Resuelve?",
    "solutions.valueGenerated": "Valor Generado",
    
    // Solution 1 - GHG Inventory
    "sol1.title": "Inventario de GEI: La Radiografía de la Contaminación",
    "sol1.subtitle": "Gases de Efecto Invernadero",
    "sol1.whatIs": "Es como hacer una \"radiografía de la contaminación\" de la empresa - descubrir cuántos gases contaminantes (como CO₂) emite en todo lo que hace.",
    "sol1.problem": "Sin saber cuánto contamina, una empresa es como alguien tratando de adelgazar sin pesarse. No puedes mejorar lo que no mides.",
    "sol1.exampleTitle": "Ejemplo Simple",
    "sol1.exampleIntro": "Una empresa de transporte tiene 100 camiones. Domani SaaS mide automáticamente:",
    "sol1.exampleItem1": "Alcance 1: Combustible quemado por los camiones (emisión directa)",
    "sol1.exampleItem2": "Alcance 2: Energía eléctrica usada en las oficinas",
    "sol1.exampleItem3": "Alcance 3: Emisiones de proveedores de neumáticos, piezas, hasta la producción del diésel",
    "sol1.benefit1": "Ahorro de tiempo: Reduce 70% del tiempo en informes",
    "sol1.benefit2": "Dinero: Evita multas y prepara para leyes obligatorias (2025/2026)",
    "sol1.benefit3": "Competitividad: Grandes empresas solo trabajan con proveedores que miden sus emisiones",

    // Solution 2 - ESG
    "sol2.title": "ESG: La Libreta de Calificaciones de la Empresa",
    "sol2.subtitle": "Ambiental, Social y Gobernanza",
    "sol2.whatIs": "Es una \"libreta de calificaciones\" de la empresa en 3 materias: medio ambiente, trato a las personas y ética en los negocios.",
    "sol2.problem": "Inversores, bancos y grandes clientes solo hacen negocios con empresas \"bien comportadas\". Sin ESG, quedas fuera de contratos millonarios.",
    "sol2.exampleTitle": "Ejemplo Simple",
    "sol2.exampleIntro": "Una fábrica de muebles quiere vender a una gran cadena de tiendas. La cadena exige:",
    "sol2.exampleItem1": "E (Ambiental): Demuestra que no deforesta ilegalmente",
    "sol2.exampleItem2": "S (Social): Muestra que no usa trabajo infantil",
    "sol2.exampleItem3": "G (Gobernanza): Tener procesos transparentes y anticorrupción",
    "sol2.benefit1": "Acceso a crédito: Bancos ofrecen intereses 20-30% menores para empresas ESG",
    "sol2.benefit2": "Nuevos contratos: 66% de las grandes empresas globales solo compran a proveedores sostenibles",
    "sol2.benefit3": "Imagen: Consumidores pagan hasta 15% más por productos de marcas sostenibles",

    // Solution 3 - Neutralization
    "sol3.title": "Neutralización de Emisiones: Saldando tu Deuda",
    "sol3.subtitle": "Compensación de Carbono",
    "sol3.whatIs": "Es como plantar árboles para \"compensar\" la contaminación que no puedes eliminar todavía.",
    "sol3.problem": "Incluso haciendo lo mejor posible, toda empresa aún contamina un poco. La compensación salda esa cuenta y permite decir \"soy carbono neutro\".",
    "sol3.exampleTitle": "Ejemplo Simple",
    "sol3.exampleIntro": "Una empresa de eventos calcula que un festival emitió 50 toneladas de CO₂ (transporte del público, energía, etc.):",
    "sol3.exampleItem1": "Por Domani SaaS, compra \"créditos de carbono\" equivalentes a 50 toneladas",
    "sol3.exampleItem2": "Cada crédito representa 1 tonelada de CO₂ evitada o removida de la atmósfera",
    "sol3.exampleItem3": "Los créditos vienen de proyectos de reforestación o energía limpia",
    "sol3.benefit1": "Marketing poderoso: \"Evento Carbono Neutro\" atrae +40% de público consciente",
    "sol3.benefit2": "Diferenciación: Se convierte en caso de éxito y referencia del sector",
    "sol3.benefit3": "Preparación legal: Anticiparse a las leyes del mercado de carbono",

    // Solution 4 - Carbon Credits
    "sol4.title": "Proyectos de Créditos de Carbono: Transformando Sostenibilidad en Ingresos",
    "sol4.subtitle": "Monetiza tus Acciones Verdes",
    "sol4.whatIs": "Transformar acciones sostenibles en \"moneda ambiental\" que puede venderse.",
    "sol4.problem": "Propiedades rurales/empresas con bosques o proyectos de energía limpia tienen un activo parado - pueden generar nuevos ingresos vendiendo créditos.",
    "sol4.exampleTitle": "Ejemplo Simple",
    "sol4.exampleIntro": "Un granjero tiene 200 hectáreas de bosque preservado. Moss Forest by Domani:",
    "sol4.exampleItem1": "Mide cuánto CO₂ absorbe ese bosque por año",
    "sol4.exampleItem2": "Certifica el proyecto (VERRA, CPR Verde)",
    "sol4.exampleItem3": "Genera créditos de carbono y vende a empresas que necesitan compensar emisiones",
    "sol4.benefit1": "Ingresos recurrentes: Los créditos pueden venderse anualmente",
    "sol4.benefit2": "Valorización: Tierra con proyecto de carbono vale 30-50% más",
    "sol4.benefit3": "Conservación: Incentivo financiero para no deforestar",
    "sol4.highlight": "Resultado: El granjero gana $100,000/año sin talar un árbol.",

    // Solution 5 - Monitoring
    "sol5.title": "Monitoreo por Satélite: Vigilancia Inteligente",
    "sol5.subtitle": "Vigilancia por Satélite + IA",
    "sol5.whatIs": "Uso de satélites e inteligencia artificial para vigilar fincas en tiempo real y validar documentos ambientales.",
    "sol5.problem": "Evitar fraudes (granjero dice que preserva, pero deforesta a escondidas) y eliminar inspecciones presenciales caras y lentas.",
    "sol5.exampleTitle": "Ejemplo Simple",
    "sol5.exampleIntro": "Un banco va a prestar $2 millones a un productor de soja. Antes de liberar el dinero, usa Moss Forest by Domani para:",
    "sol5.exampleItem1": "Ver por satélite si el área está realmente preservada",
    "sol5.exampleItem2": "Verificar si tiene CAR (Registro Ambiental Rural) válido",
    "sol5.exampleItem3": "Alertar automáticamente si hay deforestación durante el contrato",
    "sol5.benefit1": "Reducción de costos: Elimina 90% de las inspecciones físicas",
    "sol5.benefit2": "Velocidad: Análisis que tomaba 30 días cae a 24 horas",
    "sol5.benefit3": "Seguridad: Monitoreo continuo vs. inspección única anual",
    "sol5.highlight": "Resultado: Banco reduce riesgo de prestar a quien contamina (y sufrir multa).",

    // Solution 6 - Agricultural Credit
    "sol6.title": "Análisis de Crédito Agrícola: Alianza Inteligente",
    "sol6.subtitle": "Crédito Inteligente y Sostenible",
    "sol6.whatIs": "Es como tener un \"consultor inteligente que no solo aprueba o rechaza, sino que ayuda al agricultor a organizarse\". Domani hace análisis documental automático y, si algo está incompleto o mal, sugiere exactamente qué hacer para corregirlo. Así, agente de crédito y agricultor trabajan juntos para crear una relación de confianza y alianza.",
    "sol6.problem": "Los bancos tardan 30-45 días analizando documentos manualmente. Si están incompletos, el agricultor recibe rechazo sin orientación, no entiende qué hizo \"mal\" y desiste. Con Domani: análisis en 24-48h, sugerencias claras de corrección, y agricultor se convierte en cliente mejor preparado.",
    "sol6.exampleTitle": "Ejemplo Detallado: Juan, Productor de Soja",
    "sol6.exampleIntro": "Juan quiere $160,000 para semillas y fertilizantes. Así funciona:",
    "sol6.exampleItem1": "Día 1: Juan envía documentos (ID, CAR, Licencia Ambiental vencida, Extracto incompleto)",
    "sol6.exampleItem2": "En 4 horas: IA analiza todo e identifica: Licencia vencida + Extracto solo 3 meses (debería ser 12)",
    "sol6.exampleItem3": "Informe inteligente: \"Status 85% completo | Perfil BUENO\" con instrucciones claras de corrección",
    "sol6.exampleItem4": "Agente llama: \"Juan, ¡estás APROBADO! Solo 2 cositas: renovar Licencia en SEMMA (15 días) y pedir extracto de 12 meses\"",
    "sol6.exampleItem5": "Días 2-10: Juan corrige todo con orientación del agente",
    "sol6.exampleItem6": "Día 11-12: Domani valida automáticamente → CRÉDITO LIBERADO en 2-3 días hábiles",
    "sol6.benefit1": "Para el Agricultor: Claridad (entiende qué hacer), orientación paso a paso, 10-12 días vs. 45 días tradicionales",
    "sol6.benefit2": "Para el Agente: No gasta 40+ horas por cliente, orienta 10-12 clientes/día (vs. 2 manualmente)",
    "sol6.benefit3": "Para el Banco: Reduce morosidad en 35%, analiza 5-10x más clientes, conformidad ESG comprobada",
    "sol6.highlight": "Resultado: Tiempo de análisis cae 92% (45 días → 2-3 días), productividad 5x mayor, satisfacción del agricultor sube de 60% a 95%.",

    // HowItWorks Section
    "how.sectionTitle": "¿Cómo Se Conecta Todo?",
    "how.sectionSubtitle": "Piensa en Domani como una",
    "how.sectionSubtitleBold": "plataforma completa de sostenibilidad",
    "how.step1.title": "Mide",
    "how.step1.desc": "Tu contaminación",
    "how.step1.detail": "Inventario GEI",
    "how.step2.title": "Organiza",
    "how.step2.desc": "Tus buenas prácticas",
    "how.step2.detail": "ESG",
    "how.step3.title": "Compensa",
    "how.step3.desc": "Lo que no puedes evitar",
    "how.step3.detail": "Neutralización",
    "how.step4.title": "Monetiza",
    "how.step4.desc": "Tus acciones verdes",
    "how.step4.detail": "Créditos de Carbono",
    "how.step5.title": "Protege",
    "how.step5.desc": "Tus activos ambientales",
    "how.step5.detail": "Monitoreo",
    "how.step6.title": "Facilita",
    "how.step6.desc": "Financiamiento sostenible",
    "how.step6.detail": "Análisis de Crédito",
    "how.summaryPart1": "Domani transforma sostenibilidad de",
    "how.summaryBold1": "costo obligatorio",
    "how.summaryPart2": "en",
    "how.summaryBold2": "ventaja competitiva rentable",
    "how.summaryPart3": ", usando tecnología para hacer lo",
    "how.summaryBold3": "complejo simple",
    "how.summaryPart4": "y lo",
    "how.summaryBold4": "invisible medible",

    // WhyNow Section
    "why.badge": "Urgente",
    "why.title": "Por Qué Esto Importa",
    "why.titleHighlight": "Ahora",
    "why.reason1.title": "Leyes Llegando",
    "why.reason1.desc": "A partir de 2026, las empresas brasileñas estarán obligadas a reportar emisiones de gases de efecto invernadero.",
    "why.reason2.title": "Presión del Mercado",
    "why.reason2.desc": "Grandes empresas ya exigen sostenibilidad de proveedores. Quien no se adapta, queda fuera.",
    "why.reason3.title": "Oportunidad Financiera",
    "why.reason3.desc": "Los créditos de carbono moverán $20+ mil millones/año en Brasil. Es hora de posicionarse.",
    "why.conclusion": "El momento de actuar es",
    "why.conclusionBold": "ahora",
    "why.conclusionEnd": ". Las empresas que se anticipan ganan ventaja competitiva y evitan las prisas de último momento.",

    // Network Status
    "network.offline": "Estás sin conexión. Verifica tu conexión.",
    "network.online": "¡Conexión restaurada!",

    // Share
    "share.button": "Compartir",
    "share.copied": "¡Enlace copiado al portapapeles!",

    // Pull to Refresh
    "pullToRefresh.release": "Suelta para actualizar",
    "pullToRefresh.pull": "Tira para actualizar",
  },
  fr: {
    // Header
    "nav.guide": "Guide Complet",
    "nav.blog": "Blog",
    "nav.install": "Installer l'App",
    "nav.language": "Langue",
    
    // Hero
    "hero.badge": "Durabilité des Entreprises",
    "hero.title": "Transformez votre entreprise en référence de",
    "hero.titleHighlight": "Durabilité",
    "hero.subtitle": "Solutions complètes en ESG, inventaire des émissions, crédits carbone et neutralisation pour les entreprises qui veulent mener la transition verte.",
    "hero.cta": "Commencer",
    "hero.secondary": "En Savoir Plus",
    
    // Solutions
    "solutions.badge": "6 Piliers de la Durabilité",
    "solutions.title": "Solutions Complètes pour Votre Entreprise",
    "solutions.subtitle": "Nous offrons un écosystème intégré de services qui couvrent tous les besoins de votre parcours durable.",
    
    // How It Works
    "howItWorks.badge": "Comment Ça Marche",
    "howItWorks.title": "Votre Parcours Durable en 4 Étapes",
    "howItWorks.subtitle": "Un processus simple et efficace pour transformer votre entreprise.",
    "howItWorks.step1.title": "Diagnostic",
    "howItWorks.step1.desc": "Nous évaluons votre situation actuelle et identifions les opportunités.",
    "howItWorks.step2.title": "Planification",
    "howItWorks.step2.desc": "Nous créons une stratégie personnalisée pour vos objectifs.",
    "howItWorks.step3.title": "Mise en Œuvre",
    "howItWorks.step3.desc": "Nous exécutons les actions avec un suivi continu.",
    "howItWorks.step4.title": "Résultats",
    "howItWorks.step4.desc": "Nous mesurons les impacts et certifions vos réalisations.",
    
    // Why Now
    "whyNow.badge": "Pourquoi Maintenant?",
    "whyNow.title": "Pourquoi C'est Important Maintenant?",
    "whyNow.subtitle": "Le moment d'agir c'est maintenant. Les réglementations changent et le marché exige l'action.",
    "whyNow.cta": "Commencez Votre Parcours Aujourd'hui",
    
    // CTA
    "cta.title": "Prêt à Transformer Votre Entreprise?",
    "cta.subtitle": "Contactez-nous et découvrez comment nous pouvons aider votre entreprise à devenir une référence en durabilité.",
    "cta.button": "Contactez-Nous",
    
    // Footer
    "footer.description": "Solutions complètes en durabilité des entreprises, ESG et crédits carbone.",
    "footer.links": "Liens Utiles",
    "footer.contact": "Contact",
    "footer.rights": "Tous droits réservés.",
    
    // PWA
    "pwa.install": "Installer Domani",
    "pwa.tagline": "La durabilité à portée de main",
    "pwa.description": "Accédez au blog et aux contenus sur la durabilité, l'ESG et les crédits carbone.",
    "pwa.button": "Installer",
    "pwa.later": "Plus Tard",
    "pwa.iosTitle": "Installer sur iPhone",
    "pwa.understood": "Compris",
    "pwa.iosStep1": "Appuyez sur l'icône Partager (carré avec flèche vers le haut)",
    "pwa.iosStep2": "Faites défiler et appuyez sur \"Sur l'écran d'accueil\"",
    "pwa.iosStep3": "Appuyez sur \"Ajouter\"",
    "pwa.installApp": "Installer l'App",
    "pwa.browserMenu": "Ouvrez le menu du navigateur (3 points)",
    "pwa.tapInstall": "Appuyez sur \"Installer l'app\" ou \"Ajouter à l'écran d'accueil\"",
    "pwa.confirm": "Confirmez en appuyant sur \"Installer\"",
    "pwa.shareButton": "Partager",
    "pwa.addToHome": "Sur l'écran d'accueil",
    "pwa.add": "Ajouter",

    // Auth
    "auth.loginTitle": "Se Connecter",
    "auth.signupTitle": "Créer un Compte",
    "auth.loginSubtitle": "Accédez au panneau d'administration",
    "auth.signupSubtitle": "Créez votre compte administrateur",
    "auth.email": "Email",
    "auth.password": "Mot de passe",
    "auth.login": "Se Connecter",
    "auth.signup": "Créer un Compte",
    "auth.loading": "Chargement...",
    "auth.noAccount": "Pas de compte? Créez-en un",
    "auth.hasAccount": "Déjà un compte? Connectez-vous",
    "auth.backToSite": "← Retour au site",
    "auth.fillAllFields": "Veuillez remplir tous les champs.",
    "auth.passwordMinLength": "Le mot de passe doit contenir au moins 6 caractères.",
    "auth.loginSuccess": "Connexion réussie!",
    "auth.signupSuccess": "Compte créé avec succès!",
    "auth.invalidCredentials": "Email ou mot de passe invalide.",
    "auth.userExists": "Cet email est déjà enregistré.",
    "auth.error": "Erreur d'authentification.",

    // NotFound
    "notFound.title": "404",
    "notFound.message": "Oups! Page non trouvée",
    "notFound.backHome": "Retour à l'Accueil",

    // Blog
    "blog.badge": "Blog & Questions",
    "blog.title": "Discussions sur la",
    "blog.titleHighlight": "Durabilité",
    "blog.subtitle": "Obtenez des réponses sur l'ESG, les crédits carbone, le changement climatique et plus.",
    "blog.search": "Rechercher questions et réponses...",
    "blog.answeredQuestions": "Questions Répondues",
    "blog.questionsFound": "questions trouvées",
    "blog.questionFound": "question trouvée",
    "blog.noResults": "Aucune question trouvée avec ces filtres.",
    "blog.noQuestions": "Pas encore de questions répondues. Soyez le premier à soumettre votre question!",
    "blog.asked": "a demandé:",
    "blog.answerBy": "Réponse de Fabiano Santana - Domani:",
    "blog.showLess": "Afficher moins",
    "blog.readMore": "Lire plus",
    "blog.askQuestion": "Posez une Question",
    "blog.askSubtitle": "Nous répondrons publiquement",
    "blog.yourName": "Votre Nom",
    "blog.namePlaceholder": "Comment devons-nous vous appeler?",
    "blog.email": "Email",
    "blog.emailPlaceholder": "votre@email.com",
    "blog.phone": "Téléphone",
    "blog.phonePlaceholder": "+33 6 12 34 56 78",
    "blog.contactNote": "Veuillez fournir au moins un email ou téléphone pour le contact.",
    "blog.yourQuestion": "Votre Question",
    "blog.questionPlaceholder": "Quelle est votre question sur la durabilité, l'ESG ou le carbone?",
    "blog.submit": "Envoyer Question",
    "blog.submitting": "Envoi...",
    "blog.submitNote": "Votre question sera répondue par Fabiano Santana et publiée pour aider les autres.",
    "blog.successMessage": "Question envoyée avec succès! Nous répondrons bientôt.",
    "blog.errorMessage": "Erreur lors de l'envoi de la question. Veuillez réessayer.",
    "blog.validationError": "Veuillez remplir votre nom, question et au moins email ou téléphone.",
    "blog.translating": "Traduction du contenu...",
    "blog.translatingDesc": "Veuillez patienter pendant que nous traduisons les questions et réponses",
    "blog.breadcrumb": "Blog ESG",
    
    // Categories
    "cat.all": "Tous",
    "cat.sustainability": "Durabilité",
    "cat.esg": "ESG",
    "cat.climate": "Climat",
    "cat.emissions": "Émissions",
    "cat.carbon": "Carbone",
    "cat.energy": "Énergie",
    "cat.economy": "Économie",
    "cat.practice": "Pratique",

    // Solutions Section
    "solutions.sectionTitle": "Les 6 Piliers de la Durabilité des Entreprises",
    "solutions.sectionSubtitle": "Expliqués simplement, avec des exemples du monde réel",
    "solutions.whatIs": "Qu'est-ce Que C'est?",
    "solutions.problemSolves": "Quel Problème Résout-il?",
    "solutions.valueGenerated": "Valeur Générée",
    
    // Solution 1 - GHG Inventory
    "sol1.title": "Inventaire GES: La Radiographie de la Pollution",
    "sol1.subtitle": "Gaz à Effet de Serre",
    "sol1.whatIs": "C'est comme faire une \"radiographie de la pollution\" de l'entreprise - découvrir combien de gaz polluants (comme le CO₂) elle émet dans tout ce qu'elle fait.",
    "sol1.problem": "Sans savoir combien elle pollue, une entreprise est comme quelqu'un qui essaie de maigrir sans se peser. On ne peut pas améliorer ce qu'on ne mesure pas.",
    "sol1.exampleTitle": "Exemple Simple",
    "sol1.exampleIntro": "Une entreprise de transport a 100 camions. Domani SaaS mesure automatiquement:",
    "sol1.exampleItem1": "Scope 1: Carburant brûlé par les camions (émission directe)",
    "sol1.exampleItem2": "Scope 2: Électricité utilisée dans les bureaux",
    "sol1.exampleItem3": "Scope 3: Émissions des fournisseurs de pneus, pièces, jusqu'à la production du diesel",
    "sol1.benefit1": "Gain de temps: Réduit de 70% le temps passé sur les rapports",
    "sol1.benefit2": "Argent: Évite les amendes et prépare aux lois obligatoires (2025/2026)",
    "sol1.benefit3": "Compétitivité: Les grandes entreprises ne travaillent qu'avec des fournisseurs qui mesurent leurs émissions",

    // Solution 2 - ESG
    "sol2.title": "ESG: Le Bulletin de Notes de l'Entreprise",
    "sol2.subtitle": "Environnemental, Social et Gouvernance",
    "sol2.whatIs": "C'est un \"bulletin de notes\" de l'entreprise en 3 matières: environnement, traitement des personnes et éthique des affaires.",
    "sol2.problem": "Les investisseurs, banques et grands clients ne font affaire qu'avec des entreprises \"bien comportées\". Sans ESG, vous êtes exclus des contrats de plusieurs millions.",
    "sol2.exampleTitle": "Exemple Simple",
    "sol2.exampleIntro": "Une usine de meubles veut vendre à une grande chaîne de magasins. La chaîne exige:",
    "sol2.exampleItem1": "E (Environnemental): Prouvez que vous ne déforestez pas illégalement",
    "sol2.exampleItem2": "S (Social): Montrez que vous n'utilisez pas le travail des enfants",
    "sol2.exampleItem3": "G (Gouvernance): Ayez des processus transparents et anticorruption",
    "sol2.benefit1": "Accès au crédit: Les banques offrent des intérêts 20-30% plus bas aux entreprises ESG",
    "sol2.benefit2": "Nouveaux contrats: 66% des grandes entreprises mondiales n'achètent qu'à des fournisseurs durables",
    "sol2.benefit3": "Image: Les consommateurs paient jusqu'à 15% de plus pour des produits de marques durables",

    // Solution 3 - Neutralization
    "sol3.title": "Neutralisation des Émissions: Solder Votre Dette",
    "sol3.subtitle": "Compensation Carbone",
    "sol3.whatIs": "C'est comme planter des arbres pour \"compenser\" la pollution que vous ne pouvez pas encore éliminer.",
    "sol3.problem": "Même en faisant de son mieux, chaque entreprise pollue encore un peu. La compensation solde ce compte et permet de dire \"je suis neutre en carbone\".",
    "sol3.exampleTitle": "Exemple Simple",
    "sol3.exampleIntro": "Une entreprise d'événements calcule qu'un festival a émis 50 tonnes de CO₂ (transport du public, énergie, etc.):",
    "sol3.exampleItem1": "Via Domani SaaS, elle achète des \"crédits carbone\" équivalents à 50 tonnes",
    "sol3.exampleItem2": "Chaque crédit représente 1 tonne de CO₂ évitée ou retirée de l'atmosphère",
    "sol3.exampleItem3": "Les crédits proviennent de projets de reforestation ou d'énergie propre",
    "sol3.benefit1": "Marketing puissant: \"Événement Neutre en Carbone\" attire +40% de public conscient",
    "sol3.benefit2": "Différenciation: Devient un cas de succès et une référence du secteur",
    "sol3.benefit3": "Préparation légale: Anticiper les lois du marché du carbone",

    // Solution 4 - Carbon Credits
    "sol4.title": "Projets de Crédits Carbone: Transformer la Durabilité en Revenus",
    "sol4.subtitle": "Monétisez Vos Actions Vertes",
    "sol4.whatIs": "Transformer les actions durables en \"monnaie environnementale\" qui peut être vendue.",
    "sol4.problem": "Les propriétés rurales/entreprises avec forêts ou projets d'énergie propre ont un actif dormant - elles peuvent générer de nouveaux revenus en vendant des crédits.",
    "sol4.exampleTitle": "Exemple Simple",
    "sol4.exampleIntro": "Un agriculteur a 200 hectares de forêt préservée. Moss Forest by Domani:",
    "sol4.exampleItem1": "Mesure combien de CO₂ cette forêt absorbe par an",
    "sol4.exampleItem2": "Certifie le projet (VERRA, CPR Verde)",
    "sol4.exampleItem3": "Génère des crédits carbone et les vend aux entreprises qui doivent compenser leurs émissions",
    "sol4.benefit1": "Revenus récurrents: Les crédits peuvent être vendus annuellement",
    "sol4.benefit2": "Valorisation: La terre avec projet carbone vaut 30-50% de plus",
    "sol4.benefit3": "Conservation: Incitation financière à ne pas déforester",
    "sol4.highlight": "Résultat: L'agriculteur gagne 100 000€/an sans abattre un seul arbre.",

    // Solution 5 - Monitoring
    "sol5.title": "Surveillance par Satellite: Vigilance Intelligente",
    "sol5.subtitle": "Surveillance Satellite + IA",
    "sol5.whatIs": "Utilisation de satellites et d'intelligence artificielle pour surveiller les fermes en temps réel et valider les documents environnementaux.",
    "sol5.problem": "Éviter les fraudes (l'agriculteur dit qu'il préserve, mais déforeste en secret) et éliminer les inspections sur place coûteuses et longues.",
    "sol5.exampleTitle": "Exemple Simple",
    "sol5.exampleIntro": "Une banque va prêter 2 millions € à un producteur de soja. Avant de libérer l'argent, elle utilise Moss Forest by Domani pour:",
    "sol5.exampleItem1": "Voir par satellite si la zone est vraiment préservée",
    "sol5.exampleItem2": "Vérifier si elle a un CAR (Registre Environnemental Rural) valide",
    "sol5.exampleItem3": "Alerter automatiquement s'il y a déforestation pendant le contrat",
    "sol5.benefit1": "Réduction des coûts: Élimine 90% des inspections physiques",
    "sol5.benefit2": "Vitesse: L'analyse qui prenait 30 jours tombe à 24 heures",
    "sol5.benefit3": "Sécurité: Surveillance continue vs. inspection unique annuelle",
    "sol5.highlight": "Résultat: La banque réduit le risque de prêter aux pollueurs (et de subir des amendes).",

    // Solution 6 - Agricultural Credit
    "sol6.title": "Analyse de Crédit Agricole: Partenariat Intelligent",
    "sol6.subtitle": "Crédit Intelligent et Durable",
    "sol6.whatIs": "C'est comme avoir un \"consultant intelligent qui n'approuve ou ne rejette pas seulement, mais aide l'agriculteur à s'organiser\". Domani fait une analyse documentaire automatique et, si quelque chose est incomplet ou erroné, suggère exactement quoi faire pour corriger. Ainsi, agent de crédit et agriculteur travaillent ensemble pour construire une relation de confiance et de partenariat.",
    "sol6.problem": "Les banques mettent 30-45 jours à analyser les documents manuellement. S'ils sont incomplets, l'agriculteur reçoit un refus sans orientation, ne comprend pas ce qu'il a fait \"de mal\" et abandonne. Avec Domani: analyse en 24-48h, suggestions de correction claires, et l'agriculteur devient un client mieux préparé.",
    "sol6.exampleTitle": "Exemple Détaillé: Jean, Producteur de Soja",
    "sol6.exampleIntro": "Jean veut 160 000€ pour des semences et engrais. Voici comment ça marche:",
    "sol6.exampleItem1": "Jour 1: Jean envoie les documents (ID, CAR, Licence Environnementale expirée, Relevé incomplet)",
    "sol6.exampleItem2": "En 4 heures: L'IA analyse tout et identifie: Licence expirée + Relevé seulement 3 mois (devrait être 12)",
    "sol6.exampleItem3": "Rapport intelligent: \"Statut 85% complet | Profil BON\" avec instructions de correction claires",
    "sol6.exampleItem4": "L'agent appelle: \"Jean, vous êtes APPROUVÉ! Juste 2 petites choses: renouveler la Licence à la SEMMA (15 jours) et demander un relevé de 12 mois\"",
    "sol6.exampleItem5": "Jours 2-10: Jean corrige tout avec les conseils de l'agent",
    "sol6.exampleItem6": "Jour 11-12: Domani valide automatiquement → CRÉDIT LIBÉRÉ en 2-3 jours ouvrables",
    "sol6.benefit1": "Pour l'Agriculteur: Clarté (comprend quoi faire), orientation étape par étape, 10-12 jours vs. 45 jours traditionnels",
    "sol6.benefit2": "Pour l'Agent: Ne passe pas 40+ heures par client, guide 10-12 clients/jour (vs. 2 manuellement)",
    "sol6.benefit3": "Pour la Banque: Réduit les défauts de 35%, analyse 5-10x plus de clients, conformité ESG prouvée",
    "sol6.highlight": "Résultat: Le temps d'analyse chute de 92% (45 jours → 2-3 jours), productivité 5x plus élevée, satisfaction de l'agriculteur passe de 60% à 95%.",

    // HowItWorks Section
    "how.sectionTitle": "Comment Tout Cela Se Connecte?",
    "how.sectionSubtitle": "Pensez à Domani comme une",
    "how.sectionSubtitleBold": "plateforme complète de durabilité",
    "how.step1.title": "Mesure",
    "how.step1.desc": "Votre pollution",
    "how.step1.detail": "Inventaire GES",
    "how.step2.title": "Organise",
    "how.step2.desc": "Vos bonnes pratiques",
    "how.step2.detail": "ESG",
    "how.step3.title": "Compense",
    "how.step3.desc": "Ce que vous ne pouvez éviter",
    "how.step3.detail": "Neutralisation",
    "how.step4.title": "Monétise",
    "how.step4.desc": "Vos actions vertes",
    "how.step4.detail": "Crédits Carbone",
    "how.step5.title": "Protège",
    "how.step5.desc": "Vos actifs environnementaux",
    "how.step5.detail": "Surveillance",
    "how.step6.title": "Facilite",
    "how.step6.desc": "Le financement durable",
    "how.step6.detail": "Analyse de Crédit",
    "how.summaryPart1": "Domani transforme la durabilité de",
    "how.summaryBold1": "coût obligatoire",
    "how.summaryPart2": "en",
    "how.summaryBold2": "avantage compétitif rentable",
    "how.summaryPart3": ", utilisant la technologie pour rendre le",
    "how.summaryBold3": "complexe simple",
    "how.summaryPart4": "et l'",
    "how.summaryBold4": "invisible mesurable",

    // WhyNow Section
    "why.badge": "Urgent",
    "why.title": "Pourquoi C'est Important",
    "why.titleHighlight": "Maintenant",
    "why.reason1.title": "Lois Arrivant",
    "why.reason1.desc": "À partir de 2026, les entreprises brésiliennes seront obligées de déclarer leurs émissions de gaz à effet de serre.",
    "why.reason2.title": "Pression du Marché",
    "why.reason2.desc": "Les grandes entreprises exigent déjà la durabilité de leurs fournisseurs. Ceux qui ne s'adaptent pas sont exclus.",
    "why.reason3.title": "Opportunité Financière",
    "why.reason3.desc": "Les crédits carbone représenteront 20+ milliards €/an au Brésil. C'est le moment de se positionner.",
    "why.conclusion": "Le moment d'agir c'est",
    "why.conclusionBold": "maintenant",
    "why.conclusionEnd": ". Les entreprises qui anticipent gagnent un avantage compétitif et évitent la précipitation de dernière minute.",

    // Network Status
    "network.offline": "Vous êtes hors ligne. Vérifiez votre connexion.",
    "network.online": "Connexion rétablie !",

    // Share
    "share.button": "Partager",
    "share.copied": "Lien copié dans le presse-papiers !",

    // Pull to Refresh
    "pullToRefresh.release": "Relâchez pour actualiser",
    "pullToRefresh.pull": "Tirez pour actualiser",
  },
  zh: {
    // Header
    "nav.guide": "完整指南",
    "nav.blog": "博客",
    "nav.install": "安装应用",
    "nav.language": "语言",
    
    // Hero
    "hero.badge": "企业可持续发展",
    "hero.title": "将您的企业打造成",
    "hero.titleHighlight": "可持续发展",
    "hero.subtitle": "为希望引领绿色转型的企业提供ESG、排放清单、碳信用和碳中和的完整解决方案。",
    "hero.cta": "立即开始",
    "hero.secondary": "了解更多",
    
    // Solutions
    "solutions.badge": "可持续发展的6大支柱",
    "solutions.title": "为您的企业提供完整解决方案",
    "solutions.subtitle": "我们提供一个涵盖您可持续发展旅程所有需求的综合服务生态系统。",
    
    // How It Works
    "howItWorks.badge": "运作方式",
    "howItWorks.title": "您的可持续发展之旅分4步完成",
    "howItWorks.subtitle": "一个简单高效的流程来转变您的企业。",
    "howItWorks.step1.title": "诊断",
    "howItWorks.step1.desc": "我们评估您的当前状况并识别机会。",
    "howItWorks.step2.title": "规划",
    "howItWorks.step2.desc": "我们为您的目标制定个性化策略。",
    "howItWorks.step3.title": "实施",
    "howItWorks.step3.desc": "我们执行行动并持续监控。",
    "howItWorks.step4.title": "成果",
    "howItWorks.step4.desc": "我们衡量影响并认证您的成就。",
    
    // Why Now
    "whyNow.badge": "为什么是现在？",
    "whyNow.title": "为什么现在很重要？",
    "whyNow.subtitle": "行动的时刻就是现在。法规正在改变，市场需要行动。",
    "whyNow.cta": "今天就开始您的旅程",
    
    // CTA
    "cta.title": "准备好转变您的企业了吗？",
    "cta.subtitle": "联系我们，了解我们如何帮助您的企业成为可持续发展的标杆。",
    "cta.button": "联系我们",
    
    // Footer
    "footer.description": "企业可持续发展、ESG和碳信用的完整解决方案。",
    "footer.links": "有用链接",
    "footer.contact": "联系方式",
    "footer.rights": "版权所有。",
    
    // PWA
    "pwa.install": "安装多曼尼",
    "pwa.tagline": "可持续发展触手可及",
    "pwa.description": "访问关于可持续发展、ESG和碳信用的博客和内容。",
    "pwa.button": "安装",
    "pwa.later": "稍后",
    "pwa.iosTitle": "在iPhone上安装",
    "pwa.understood": "明白了",
    "pwa.iosStep1": "点击分享图标（带向上箭头的方框）",
    "pwa.iosStep2": "滚动并点击添加到主屏幕",
    "pwa.iosStep3": "点击添加",
    "pwa.installApp": "安装应用",
    "pwa.browserMenu": "打开浏览器菜单（3个点）",
    "pwa.tapInstall": "点击安装应用或添加到主屏幕",
    "pwa.confirm": "点击安装确认",
    "pwa.shareButton": "分享",
    "pwa.addToHome": "添加到主屏幕",
    "pwa.add": "添加",

    // Auth
    "auth.loginTitle": "登录面板",
    "auth.signupTitle": "创建账户",
    "auth.loginSubtitle": "访问管理面板",
    "auth.signupSubtitle": "创建您的管理员账户",
    "auth.email": "电子邮件",
    "auth.password": "密码",
    "auth.login": "登录",
    "auth.signup": "创建账户",
    "auth.loading": "加载中...",
    "auth.noAccount": "没有账户？创建一个",
    "auth.hasAccount": "已有账户？登录",
    "auth.backToSite": "← 返回网站",
    "auth.fillAllFields": "请填写所有字段。",
    "auth.passwordMinLength": "密码必须至少6个字符。",
    "auth.loginSuccess": "登录成功！",
    "auth.signupSuccess": "账户创建成功！",
    "auth.invalidCredentials": "电子邮件或密码无效。",
    "auth.userExists": "此电子邮件已注册。",
    "auth.error": "认证错误。",

    // NotFound
    "notFound.title": "404",
    "notFound.message": "哎呀！页面未找到",
    "notFound.backHome": "返回首页",

    // Blog
    "blog.badge": "博客与问答",
    "blog.title": "关于",
    "blog.titleHighlight": "可持续发展",
    "blog.subtitle": "获取关于ESG、碳信用、气候变化等问题的解答。",
    "blog.search": "搜索问题和答案...",
    "blog.answeredQuestions": "已回答的问题",
    "blog.questionsFound": "个问题找到",
    "blog.questionFound": "个问题找到",
    "blog.noResults": "使用这些过滤条件没有找到问题。",
    "blog.noQuestions": "还没有已回答的问题。成为第一个提交问题的人！",
    "blog.asked": "提问：",
    "blog.answerBy": "Fabiano Santana - Domani 的回答：",
    "blog.showLess": "显示更少",
    "blog.readMore": "阅读更多",
    "blog.askQuestion": "提出问题",
    "blog.askSubtitle": "我们将公开回答",
    "blog.yourName": "您的姓名",
    "blog.namePlaceholder": "我们应该怎么称呼您？",
    "blog.email": "电子邮件",
    "blog.emailPlaceholder": "your@email.com",
    "blog.phone": "电话",
    "blog.phonePlaceholder": "+86 138 0000 0000",
    "blog.contactNote": "请至少提供电子邮件或电话以便联系。",
    "blog.yourQuestion": "您的问题",
    "blog.questionPlaceholder": "您关于可持续发展、ESG或碳的问题是什么？",
    "blog.submit": "提交问题",
    "blog.submitting": "提交中...",
    "blog.submitNote": "您的问题将由Fabiano Santana回答并发布以帮助他人。",
    "blog.successMessage": "问题提交成功！我们会尽快回复。",
    "blog.errorMessage": "提交问题时出错。请重试。",
    "blog.validationError": "请填写您的姓名、问题以及至少电子邮件或电话。",
    "blog.translating": "正在翻译内容...",
    "blog.translatingDesc": "请稍候，我们正在翻译问题和答案",
    "blog.breadcrumb": "ESG博客",
    
    // Categories
    "cat.all": "全部",
    "cat.sustainability": "可持续发展",
    "cat.esg": "ESG",
    "cat.climate": "气候",
    "cat.emissions": "排放",
    "cat.carbon": "碳",
    "cat.energy": "能源",
    "cat.economy": "经济",
    "cat.practice": "实践",

    // Solutions Section
    "solutions.sectionTitle": "企业可持续发展的6大支柱",
    "solutions.sectionSubtitle": "以简单的方式解释，配以真实案例",
    "solutions.whatIs": "什么是？",
    "solutions.problemSolves": "解决什么问题？",
    "solutions.valueGenerated": "创造的价值",
    
    // Solution 1 - GHG Inventory
    "sol1.title": "温室气体清单：污染X光",
    "sol1.subtitle": "温室气体",
    "sol1.whatIs": "就像对公司进行污染X光——了解公司在所有活动中排放了多少污染气体（如CO2）。",
    "sol1.problem": "不知道排放多少的公司就像不称体重就想减肥的人。你无法改善你不衡量的东西。",
    "sol1.exampleTitle": "简单示例",
    "sol1.exampleIntro": "一家运输公司有100辆卡车。Domani SaaS自动测量：",
    "sol1.exampleItem1": "范围1：卡车燃烧的燃料（直接排放）",
    "sol1.exampleItem2": "范围2：办公室使用的电力",
    "sol1.exampleItem3": "范围3：轮胎供应商、零部件甚至柴油生产的排放",
    "sol1.benefit1": "节省时间：减少70%的报告时间",
    "sol1.benefit2": "节省资金：避免罚款并为强制性法律做准备（2025/2026）",
    "sol1.benefit3": "竞争力：大公司只与测量排放的供应商合作",

    // Solution 2 - ESG
    "sol2.title": "ESG：公司的成绩单",
    "sol2.subtitle": "环境、社会和治理",
    "sol2.whatIs": "这是公司在3个科目上的成绩单：环境、人员待遇和商业道德。",
    "sol2.problem": "投资者、银行和大客户只与表现良好的公司做生意。没有ESG，您将被排除在百万美元合同之外。",
    "sol2.exampleTitle": "简单示例",
    "sol2.exampleIntro": "一家家具厂想向大型零售连锁店销售。连锁店要求：",
    "sol2.exampleItem1": "E（环境）：证明您没有非法砍伐森林",
    "sol2.exampleItem2": "S（社会）：证明您不使用童工",
    "sol2.exampleItem3": "G（治理）：拥有透明和反腐败流程",
    "sol2.benefit1": "获得信贷：银行为ESG公司提供20-30%的较低利率",
    "sol2.benefit2": "新合同：66%的大型全球公司只从可持续供应商采购",
    "sol2.benefit3": "形象：消费者愿意为可持续品牌产品多付15%",

    // Solution 3 - Neutralization
    "sol3.title": "排放中和：清零您的债务",
    "sol3.subtitle": "碳抵消",
    "sol3.whatIs": "就像种树来抵消您还无法消除的污染。",
    "sol3.problem": "即使做到最好，每家公司仍会有一些污染。抵消可以清零这个账户，让您可以说我是碳中和的。",
    "sol3.exampleTitle": "简单示例",
    "sol3.exampleIntro": "一家活动公司计算出一个节日排放了50吨CO2（观众交通、能源等）：",
    "sol3.exampleItem1": "通过Domani SaaS，购买相当于50吨的碳信用",
    "sol3.exampleItem2": "每个信用代表1吨避免或从大气中移除的CO2",
    "sol3.exampleItem3": "信用来自植树造林或清洁能源项目",
    "sol3.benefit1": "强大的营销：碳中和活动吸引+40%的有意识观众",
    "sol3.benefit2": "差异化：成为成功案例和行业标杆",
    "sol3.benefit3": "法律准备：提前应对碳市场法律",

    // Solution 4 - Carbon Credits
    "sol4.title": "碳信用项目：将可持续发展转化为收入",
    "sol4.subtitle": "将您的绿色行动货币化",
    "sol4.whatIs": "将可持续行动转化为可以出售的环境货币。",
    "sol4.problem": "拥有森林或清洁能源项目的农村地产/公司拥有闲置资产——他们可以通过出售信用产生新收入。",
    "sol4.exampleTitle": "简单示例",
    "sol4.exampleIntro": "一位农民拥有200公顷的保护森林。Moss Forest by Domani：",
    "sol4.exampleItem1": "测量该森林每年吸收多少CO₂",
    "sol4.exampleItem2": "认证项目（VERRA, CPR Verde）",
    "sol4.exampleItem3": "生成碳信用并出售给需要抵消排放的公司",
    "sol4.benefit1": "经常性收入：信用可以每年出售",
    "sol4.benefit2": "增值：拥有碳项目的土地价值提高30-50%",
    "sol4.benefit3": "保护：不砍伐森林的经济激励",
    "sol4.highlight": "结果：农民每年赚取10万美元而不砍伐一棵树。",

    // Solution 5 - Monitoring
    "sol5.title": "卫星监测：智能监控",
    "sol5.subtitle": "卫星监控 + 人工智能",
    "sol5.whatIs": "使用卫星和人工智能实时监控农场并验证环境文件。",
    "sol5.problem": "避免欺诈（农民说他们在保护，但秘密砍伐森林）并消除昂贵且耗时的现场检查。",
    "sol5.exampleTitle": "简单示例",
    "sol5.exampleIntro": "一家银行将向大豆生产商贷款200万美元。在放款前，使用Moss Forest by Domani：",
    "sol5.exampleItem1": "通过卫星查看该地区是否真的得到保护",
    "sol5.exampleItem2": "验证是否有有效的CAR（农村环境登记）",
    "sol5.exampleItem3": "如果合同期间发生森林砍伐自动发出警报",
    "sol5.benefit1": "成本降低：消除90%的实地检查",
    "sol5.benefit2": "速度：30天的分析缩短到24小时",
    "sol5.benefit3": "安全性：持续监控 vs 每年一次检查",
    "sol5.highlight": "结果：银行降低向污染者贷款（并遭受罚款）的风险。",

    // Solution 6 - Agricultural Credit
    "sol6.title": "农业信贷分析：智能伙伴关系",
    "sol6.subtitle": "智能可持续信贷",
    "sol6.whatIs": "就像拥有一个不仅仅批准或拒绝，而是帮助农民组织起来的智能顾问。Domani进行自动文档分析，如果有什么不完整或错误，会建议如何纠正。这样，信贷代理和农民一起工作，建立信任和伙伴关系。",
    "sol6.problem": "银行手动分析文件需要30-45天。如果不完整，农民收到拒绝却没有指导，不明白自己做错了什么就放弃了。使用Domani：24-48小时内分析，明确的纠正建议，农民成为准备更充分的客户。",
    "sol6.exampleTitle": "详细示例：大豆生产商张先生",
    "sol6.exampleIntro": "张先生想要16万美元购买种子和肥料。运作方式如下：",
    "sol6.exampleItem1": "第1天：张先生发送文件（身份证、CAR、过期的环境许可证、不完整的声明）",
    "sol6.exampleItem2": "4小时内：AI分析一切并识别：许可证过期 + 声明只有3个月（应该是12个月）",
    "sol6.exampleItem3": "智能报告：状态85%完成 | 良好档案，附有明确的纠正说明",
    "sol6.exampleItem4": "代理打电话：张先生，您已批准！只需要2件小事：在SEMMA更新许可证（15天）并要求12个月的声明",
    "sol6.exampleItem5": "第2-10天：张先生在代理指导下纠正一切",
    "sol6.exampleItem6": "第11-12天：Domani自动验证 → 信贷在2-3个工作日内发放",
    "sol6.benefit1": "对农民：清晰（了解该做什么）、分步指导、10-12天 vs 传统45天",
    "sol6.benefit2": "对代理：不用在每个客户上花费40+小时、每天指导10-12个客户（vs 手动2个）",
    "sol6.benefit3": "对银行：违约率降低35%、分析客户数量增加5-10倍、ESG合规得到证明",
    "sol6.highlight": "结果：分析时间减少92%（45天 → 2-3天）、生产力提高5倍、农民满意度从60%上升到95%。",

    // HowItWorks Section
    "how.sectionTitle": "这一切如何连接？",
    "how.sectionSubtitle": "将Domani视为一个",
    "how.sectionSubtitleBold": "完整的可持续发展平台",
    "how.step1.title": "测量",
    "how.step1.desc": "您的污染",
    "how.step1.detail": "温室气体清单",
    "how.step2.title": "组织",
    "how.step2.desc": "您的最佳实践",
    "how.step2.detail": "ESG",
    "how.step3.title": "抵消",
    "how.step3.desc": "您无法避免的",
    "how.step3.detail": "中和",
    "how.step4.title": "货币化",
    "how.step4.desc": "您的绿色行动",
    "how.step4.detail": "碳信用",
    "how.step5.title": "保护",
    "how.step5.desc": "您的环境资产",
    "how.step5.detail": "监测",
    "how.step6.title": "促进",
    "how.step6.desc": "可持续融资",
    "how.step6.detail": "信贷分析",
    "how.summaryPart1": "Domani将可持续发展从",
    "how.summaryBold1": "强制成本",
    "how.summaryPart2": "转变为",
    "how.summaryBold2": "有利可图的竞争优势",
    "how.summaryPart3": "，使用技术使",
    "how.summaryBold3": "复杂变简单",
    "how.summaryPart4": "使",
    "how.summaryBold4": "不可见变可测量",

    // WhyNow Section
    "why.badge": "紧急",
    "why.title": "为什么这很重要",
    "why.titleHighlight": "现在",
    "why.reason1.title": "法律即将到来",
    "why.reason1.desc": "从2026年起，巴西公司将被要求报告温室气体排放。",
    "why.reason2.title": "市场压力",
    "why.reason2.desc": "大公司已经要求供应商实现可持续发展。不适应的将被淘汰。",
    "why.reason3.title": "财务机会",
    "why.reason3.desc": "碳信用每年将在巴西流动200亿美元以上。是时候定位自己了。",
    "why.conclusion": "行动的时刻是",
    "why.conclusionBold": "现在",
    "why.conclusionEnd": "。提前行动的公司获得竞争优势，避免最后一刻的仓促。",

    // Network Status
    "network.offline": "您已离线。请检查您的连接。",
    "network.online": "连接已恢复！",

    // Share
    "share.button": "分享",
    "share.copied": "链接已复制到剪贴板！",

    // Pull to Refresh
    "pullToRefresh.release": "松开以刷新",
    "pullToRefresh.pull": "下拉以刷新",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("domani-language");
    if (saved && ["pt", "en", "es", "fr", "zh"].includes(saved)) {
      return saved as Language;
    }
    return detectSystemLanguage();
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayLanguage, setDisplayLanguage] = useState(language);

  const handleSetLanguage = useCallback((lang: Language) => {
    if (lang === language) return;
    
    setIsTransitioning(true);
    localStorage.setItem("domani-language", lang);
    
    // Start fade out, then change language, then fade in
    setTimeout(() => {
      setLanguage(lang);
      setDisplayLanguage(lang);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 150);
  }, [language]);

  const t = (key: string): string => {
    return translations[displayLanguage][key] || translations.pt[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t, isTransitioning }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={displayLanguage}
          initial={{ opacity: 0.7 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0.7 }}
          transition={{ duration: 0.15, ease: "easeInOut" }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
